import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import InterestPickerModal from './InterestPickerModal';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

export default function SettingsPanel({ onLogout, onInterestsChange }) {
  const { accessToken: token } = useAuth();
  const [open, setOpen]             = useState(false);
  const [view, setView]             = useState(null); // null | 'password' | 'location'
  const [currentPw, setCurrentPw]   = useState('');
  const [newPw, setNewPw]           = useState('');
  const [confirmPw, setConfirmPw]   = useState('');
  const [zipcode, setZipcode]       = useState('');
  const [state, setState]           = useState('');
  const [msg, setMsg]               = useState('');
  const [loading, setLoading]       = useState(false);
  const [interestModalOpen, setInterestModalOpen] = useState(false);
  const [myInterests, setMyInterests]             = useState([]);

  // Load current interests on mount (reader accounts only)
  useEffect(() => {
    if (localStorage.getItem('userType') !== 'user') return;
    axios.get('/api/users/me', {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      setMyInterests(res.data.interests || []);
    }).catch(() => {});
  }, []);

  const reset = () => {
    setCurrentPw(''); setNewPw(''); setConfirmPw('');
    setZipcode(''); setState(''); setMsg('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMsg('');
    if (newPw !== confirmPw) { setMsg('New passwords do not match.'); return; }
    if (newPw.length < 6)    { setMsg('New password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/change-password',
        { currentPassword: currentPw, newPassword: newPw },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg('Password updated successfully.');
      reset();
      setTimeout(() => { setMsg(''); setView(null); }, 2000);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    setMsg('');
    if (!/^\d{5}$/.test(zipcode)) { setMsg('Enter a valid 5-digit ZIP code.'); return; }
    if (!state)                    { setMsg('Please select a state.'); return; }
    setLoading(true);
    try {
      await axios.patch('/api/users/me/location',
        { zipcode, state },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg('Location updated.');
      reset();
      setTimeout(() => { setMsg(''); setView(null); }, 2000);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to update location.');
    } finally {
      setLoading(false);
    }
  };

  const isSuccess = msg === 'Password updated successfully.' || msg === 'Location updated.';

  const handleSaveInterests = async (interests) => {
    try {
      await axios.patch('/api/users/me/interests',
        { interests },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMyInterests(interests);
      if (onInterestsChange) onInterestsChange(interests);
    } catch (err) {
      console.error('Failed to save interests', err);
    } finally {
      setInterestModalOpen(false);
    }
  };

  return (
    <div style={{
      borderTop: '1px solid #2a2a2a',
      marginTop: 'auto',
      paddingTop: '0.85rem',
    }}>
      {/* Settings header row */}
      <div
        onClick={() => { setOpen(o => !o); if (open) { setView(null); reset(); } }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', userSelect: 'none', padding: '0.3rem 0.5rem',
          borderRadius: '6px', color: '#aaa', fontSize: '0.82rem', fontWeight: '600',
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#1f1f1f'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <span>⚙️ Settings</span>
        <span style={{ fontSize: '0.7rem' }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ marginTop: '0.5rem' }}>

          {/* Change Password option */}
          <div
            onClick={() => { setView(view === 'password' ? null : 'password'); reset(); }}
            style={{
              padding: '0.45rem 0.75rem', fontSize: '0.82rem', cursor: 'pointer',
              borderRadius: '6px', color: view === 'password' ? '#fff' : '#ccc',
              backgroundColor: view === 'password' ? '#1a1a1a' : 'transparent',
            }}
            onMouseEnter={e => { if (view !== 'password') e.currentTarget.style.background = '#1f1f1f'; }}
            onMouseLeave={e => { if (view !== 'password') e.currentTarget.style.background = 'transparent'; }}
          >
            🔑 Change Password
          </div>

          {/* Change Password form */}
          {view === 'password' && (
            <form onSubmit={handleChangePassword} style={{ padding: '0.75rem', backgroundColor: '#141414', borderRadius: '8px', marginTop: '0.4rem' }}>
              <input
                type="password"
                placeholder="Current password"
                value={currentPw}
                onChange={e => setCurrentPw(e.target.value)}
                required
                style={inputStyle}
              />
              <input
                type="password"
                placeholder="New password (min 6 chars)"
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                required
                style={inputStyle}
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmPw}
                onChange={e => setConfirmPw(e.target.value)}
                required
                style={inputStyle}
              />
              {msg && (
                <div style={{ fontSize: '0.76rem', marginBottom: '0.5rem', color: isSuccess ? '#28a745' : '#dc3545' }}>
                  {msg}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', padding: '0.4rem', backgroundColor: '#3366cc', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.82rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
              >
                {loading ? 'Saving…' : 'Update Password'}
              </button>
            </form>
          )}

          {/* Update Location option — readers only */}
          {localStorage.getItem('userType') === 'user' && <div
            onClick={() => { setView(view === 'location' ? null : 'location'); reset(); }}
            style={{
              padding: '0.45rem 0.75rem', fontSize: '0.82rem', cursor: 'pointer',
              borderRadius: '6px', color: view === 'location' ? '#fff' : '#ccc',
              backgroundColor: view === 'location' ? '#1a1a1a' : 'transparent',
              marginTop: '0.1rem',
            }}
            onMouseEnter={e => { if (view !== 'location') e.currentTarget.style.background = '#1f1f1f'; }}
            onMouseLeave={e => { if (view !== 'location') e.currentTarget.style.background = 'transparent'; }}
          >
            📍 Update Location
          </div>}

          {/* Update Location form */}
          {view === 'location' && (
            <form onSubmit={handleUpdateLocation} style={{ padding: '0.75rem', backgroundColor: '#141414', borderRadius: '8px', marginTop: '0.4rem' }}>
              <input
                type="text"
                placeholder="ZIP code (5 digits)"
                value={zipcode}
                onChange={e => setZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                maxLength={5}
                required
                style={inputStyle}
              />
              <select
                value={state}
                onChange={e => setState(e.target.value)}
                required
                style={{ ...inputStyle, color: state ? '#eee' : '#888' }}
              >
                <option value="">Select state…</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {msg && (
                <div style={{ fontSize: '0.76rem', marginBottom: '0.5rem', color: isSuccess ? '#28a745' : '#dc3545' }}>
                  {msg}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', padding: '0.4rem', backgroundColor: '#3366cc', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.82rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
              >
                {loading ? 'Saving…' : 'Save Location'}
              </button>
            </form>
          )}

          {/* My Interests — readers only */}
          {localStorage.getItem('userType') === 'user' && (
            <div
              onClick={() => setInterestModalOpen(true)}
              style={{
                padding: '0.45rem 0.75rem', fontSize: '0.82rem', cursor: 'pointer',
                borderRadius: '6px', color: '#ccc', marginTop: '0.1rem',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#1f1f1f'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              🎯 My Interests{myInterests.length > 0 ? ` (${myInterests.length})` : ''}
            </div>
          )}

          {/* Logout */}
          <div
            onClick={onLogout}
            style={{
              padding: '0.45rem 0.75rem', fontSize: '0.82rem', cursor: 'pointer',
              borderRadius: '6px', color: '#dc3545', marginTop: '0.25rem',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#1f1f1f'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            ↩ Log Out
          </div>

        </div>
      )}

      <InterestPickerModal
        isOpen={interestModalOpen}
        onClose={() => setInterestModalOpen(false)}
        initialInterests={myInterests}
        onSave={handleSaveInterests}
      />
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '0.4rem 0.6rem',
  marginBottom: '0.5rem',
  borderRadius: '5px',
  border: '1px solid #333',
  backgroundColor: '#1e1e1e',
  color: '#eee',
  fontSize: '0.82rem',
  boxSizing: 'border-box',
};
